'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';

import {
  getGTMPromotionSource,
  GTMPromotionSource,
  useGTMPromotionBanner
} from '@/fo/api/gtm';
import { BannerThemeEnum } from '@/fo/api/magento';
import ArrowIcon from '@/fo/icons/base/icons/arrow24.svg';
import { UiAdvertisingInformerBase } from '@/fo/ui/advertising/informer/base';
import { UiKitButtonBase } from '@/fo/ui/kit/button/base';
import { UiKitCarouselBase } from '@/fo/ui/kit/carousel/base';
import { utilGetLinkRel } from '@/fo/util/get-link-rel';

import { styles } from './UiKitBannerMini.styles';

export interface Banner {
  image: {
    sm: string;
    md: string;
  };
  alt: string;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  buttonText: string;
  link: string;
  target?: boolean;
  theme: BannerThemeEnum;
  erid?: string;
  promotionName?: string;
}

export interface UiKitBannerMiniProps {
  className?: string;
  banners: Banner[];
  autoplay?: boolean | number;
  advertisingWarningText?: string;
}

type BannerMiniSlideProps = {
  banner: Banner;
  index: number;
  slots: ReturnType<typeof styles>;
  advertisingWarningText?: string;
  onHoverChange: (isHovered: boolean) => void;
  promotionSource: GTMPromotionSource;
};

function BannerMiniSlide({
  banner,
  index,
  slots,
  advertisingWarningText,
  onHoverChange,
  promotionSource
}: BannerMiniSlideProps) {
  const { ref, trackSelectPromotion } = useGTMPromotionBanner({
    creativeName: banner.image.md || banner.image.sm,
    promotionId: banner.link,
    promotionName: banner.promotionName,
    source: promotionSource,
    index: index + 1
  });

  return (
    <div
      ref={ref}
      className={slots.banner()}
    >
      {banner.erid && (
        <UiAdvertisingInformerBase
          erid={banner.erid}
          advertisingWarningText={advertisingWarningText}
          className={slots.advertisingInformer()}
          classNameContent={slots.advertisingInformerContent()}
          onHoverChange={onHoverChange}
          sendMultiple={promotionSource === 'slider'}
        />
      )}
      <picture>
        <source
          srcSet={banner.image.sm}
          media="(max-width: 767px)"
        />
        <Image
          className={slots.image()}
          alt={banner.alt}
          src={banner.image.md}
          width={800}
          height={400}
        />
      </picture>
      <div className={slots.content()}>
        <h2
          className={slots.title({
            isDarkTheme: banner.theme === BannerThemeEnum.Dark
          })}
        >
          {banner.title}
        </h2>
        <p
          className={slots.text({
            isDarkTheme: banner.theme === BannerThemeEnum.Dark
          })}
        >
          {banner.subtitle}
        </p>
        <Link
          href={banner.link}
          target={banner.target ? '_blank' : '_self'}
          rel={utilGetLinkRel(banner.link, banner.target)}
          onClick={trackSelectPromotion}
        >
          <UiKitButtonBase
            color="ghost"
            className={slots.button({
              isDarkTheme: banner.theme === BannerThemeEnum.Dark
            })}
          >
            <div className={slots.buttonContent()}>
              {banner.buttonText}
              <ArrowIcon />
            </div>
          </UiKitButtonBase>
        </Link>
      </div>
    </div>
  );
}

export function UiKitBannerMini({
  className,
  banners,
  autoplay = false,
  advertisingWarningText
}: UiKitBannerMiniProps) {
  const slots = styles();
  const [isAdvertisingInformerActive, setIsAdvertisingInformerActive] =
    useState(false);
  const promotionSource = getGTMPromotionSource(banners.length);

  return (
    <div className={slots.base({ className })}>
      <UiKitCarouselBase
        autoplay={autoplay}
        options={{ active: banners.length > 1 }}
        className={slots.carousel()}
        showArrows={!isAdvertisingInformerActive}
        arrowType="chevron"
        arrowPrevClass={slots.arrowPrev()}
        arrowNextClass={slots.arrowNext()}
      >
        {banners.map((banner, index) => (
          <BannerMiniSlide
            key={index}
            banner={banner}
            index={index}
            slots={slots}
            advertisingWarningText={advertisingWarningText}
            onHoverChange={setIsAdvertisingInformerActive}
            promotionSource={promotionSource}
          />
        ))}
      </UiKitCarouselBase>
    </div>
  );
}
